<template>
  <div>
    <div class="mb-16">
      <strong>{{ $t('insuranceCover.selbstbeteiligung.title') }}</strong>
      <br />
      <p>{{ $t('insuranceCover.selbstbeteiligung.hint') }}</p>
    </div>

    <div class="deductible-dropdown">
      <div v-if="isTeilkasko()">
        <SelectMenue
          :key="selectMenueKey"
          :label="$t('insuranceCover.selbstbeteiligung.dropdownLabelTK')"
          :model-value="selbstbeteiligungTKvalue?.label"
          :options="chooseSelbstbeteiligungTeilkaskoDropdownItems"
          class="teilkasko-selection"
          @update:input="handleDeductibleChange"
        >
          <template v-slot:error></template>
        </SelectMenue>
      </div>
      <div v-else-if="isVollkasko()">
        <SelectMenue
          :key="selectMenueKey"
          :label="$t('insuranceCover.selbstbeteiligung.dropdownLabelVK')"
          :model-value="selbstbeteiligungVKvalue?.label"
          :options="chooseSelbstbeteiligungVollkaskoDropdownItems"
          class="vollkasko-selection"
          @update:input="handleDeductibleChange"
        >
          <template v-slot:error></template>
        </SelectMenue>
      </div>
      <div v-else>
        <BasicTextfield
          :label="$t('insuranceCover.selbstbeteiligung.noDeductible')"
          :status="statusDisabled()"
          :value="$t('insuranceCover.selbstbeteiligung.noDeductiblePlaceholder')"
          class="no-kasko"
        ></BasicTextfield>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BasicTextfield, InputStatus, SelectMenue } from 'de-zurich-ui-library'

import { onBeforeMount, type PropType, type Ref, ref, watch } from 'vue'
import {
  ComprehensiveCover,
  type Deductible,
  deductibleToString,
  type Extras
} from '@/typesTenant/MotorContractChange'

interface DeductibleOption {
  label: string
  value: Deductible
}

export interface DeductibleOptions {
  [index: number]: DeductibleOption
}

const optionsTK = [
  { label: 'Teilkasko: 0€', value: { deductiblePartiallyComprehensive: 0 } },
  { label: 'Teilkasko: 150€', value: { deductiblePartiallyComprehensive: 150 } },
  { label: 'Teilkasko: 300€', value: { deductiblePartiallyComprehensive: 300 } },
  { label: 'Teilkasko: 500€', value: { deductiblePartiallyComprehensive: 500 } }
]

const optionsVK = [
  {
    label: 'Vollkasko: 150€ + Teilkasko: 0€',
    value: { deductiblePartiallyComprehensive: 0, deductibleFullyComprehensive: 150 }
  },
  {
    label: 'Vollkasko: 150€ + Teilkasko: 150€',
    value: { deductiblePartiallyComprehensive: 150, deductibleFullyComprehensive: 150 }
  },
  {
    label: 'Vollkasko: 300€ + Teilkasko: 0€',
    value: { deductiblePartiallyComprehensive: 0, deductibleFullyComprehensive: 300 }
  },
  {
    label: 'Vollkasko: 300€ + Teilkasko: 150€',
    value: { deductiblePartiallyComprehensive: 150, deductibleFullyComprehensive: 300 }
  },
  {
    label: 'Vollkasko: 300€ + Teilkasko: 300€',
    value: { deductiblePartiallyComprehensive: 300, deductibleFullyComprehensive: 300 }
  },
  {
    label: 'Vollkasko: 500€ + Teilkasko: 0€',
    value: { deductiblePartiallyComprehensive: 0, deductibleFullyComprehensive: 500 }
  },
  {
    label: 'Vollkasko: 500€ + Teilkasko: 150€',
    value: { deductiblePartiallyComprehensive: 150, deductibleFullyComprehensive: 500 }
  },
  {
    label: 'Vollkasko: 500€ + Teilkasko: 300€',
    value: { deductiblePartiallyComprehensive: 300, deductibleFullyComprehensive: 500 }
  },
  {
    label: 'Vollkasko: 500€ + Teilkasko: 500€',
    value: { deductiblePartiallyComprehensive: 500, deductibleFullyComprehensive: 500 }
  },
  {
    label: 'Vollkasko: 1000€ + Teilkasko: 150€',
    value: { deductiblePartiallyComprehensive: 150, deductibleFullyComprehensive: 1000 }
  },
  {
    label: 'Vollkasko: 1000€ + Teilkasko: 500€',
    value: { deductiblePartiallyComprehensive: 500, deductibleFullyComprehensive: 1000 }
  }
]

const props = defineProps({
  selectedKasko: {
    type: String as PropType<ComprehensiveCover>,
    required: true
  },
  value: {
    type: Object as PropType<Deductible>,
    required: true
  }
})

const emit = defineEmits<{
  input: [val: Deductible | Extras | DeductibleOption]
}>()

const chooseSelbstbeteiligungTeilkaskoDropdownItems = optionsTK
const chooseSelbstbeteiligungVollkaskoDropdownItems = optionsVK
const selbstbeteiligungTKvalue: Ref<DeductibleOption | undefined> = ref()
const selbstbeteiligungVKvalue: Ref<DeductibleOption | undefined> = ref()
const noSelbstbeteiligung: DeductibleOption = { label: '', value: {} }

// to enforce rerendering of selectmenues to update the defaultValue display
const selectMenueKey = ref(0)

onBeforeMount(() => {
  const tkBaseValue = { deductiblePartiallyComprehensive: 0 }
  const vkBaseValue = {
    deductiblePartiallyComprehensive: 0,
    deductibleFullyComprehensive: 150
  }

  selbstbeteiligungTKvalue.value = {
    label: deductibleToString(tkBaseValue),
    value: tkBaseValue
  }
  selbstbeteiligungVKvalue.value = {
    label: deductibleToString(vkBaseValue),
    value: vkBaseValue
  }

  if (props.selectedKasko === ComprehensiveCover.PARTIALLY_COMPREHENSIVE) {
    selbstbeteiligungTKvalue.value = {
      label: deductibleToString(props.value),
      value: props.value
    }
    emit('input', selbstbeteiligungTKvalue.value)
  } else if (props.selectedKasko === ComprehensiveCover.FULLY_COMPREHENSIVE) {
    selbstbeteiligungVKvalue.value = {
      label: deductibleToString(props.value),
      value: props.value
    }
    emit('input', selbstbeteiligungVKvalue.value)
  }
})

function handleDeductibleChange(...args: unknown[]): void {
  const option = args[0] as DeductibleOption
  emitValue(option)
}

function isVollkasko(): boolean {
  return props.selectedKasko === ComprehensiveCover.FULLY_COMPREHENSIVE
}

function isTeilkasko(): boolean {
  return props.selectedKasko === ComprehensiveCover.PARTIALLY_COMPREHENSIVE
}

watch(
  () => props.selectedKasko,
  () => {
    setSelectedValueAtKaskoChange()
  }
)

function setSelectedValueAtKaskoChange(): void {
  if (props.selectedKasko === ComprehensiveCover.FULLY_COMPREHENSIVE) {
    selectMenueKey.value += 1
    emitValue(selbstbeteiligungVKvalue.value)
  } else if (props.selectedKasko === ComprehensiveCover.PARTIALLY_COMPREHENSIVE) {
    selectMenueKey.value += 1
    emitValue(selbstbeteiligungTKvalue.value)
  } else {
    emitValue(noSelbstbeteiligung)
  }
}

function emitValue(option: DeductibleOption | undefined): void {
  if (!option) return
  emit('input', option)
}

function statusDisabled(): InputStatus {
  return InputStatus.DISABLED
}
</script>

<style lang="scss" scoped>
.text {
  font-size: var(--small-font-size);
  color: var(--primary-color-0);
  font-family: var(--f-primary-base), serif;
  padding-bottom: 16px;
}

.deductible-dropdown {
  width: 100%;
  @media (min-width: 600px) {
    max-width: 500px;
  }
}
</style>
